import { getAuth } from 'firebase/auth';
import { getApiBaseUrlWithFallback } from '../config/environment';
import { app } from '../config/firebaseConfig';
import { getImpersonationHeaders } from '../context/ImpersonationContext';

const INVITATION_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  WITHDRAWN: 'withdrawn',
  WAITLIST: 'waitlist',
  PROMOTED: 'promoted',
};

export const getInvitationStatusDisplay = (status) => {
  switch (status) {
    case INVITATION_STATUS.PENDING:
      return { label: 'Pending', color: 'default', description: 'Invitation not yet sent' };
    case INVITATION_STATUS.SENT:
      return { label: 'Sent', color: 'info', description: 'Awaiting response' };
    case INVITATION_STATUS.ACCEPTED:
      return { label: 'Accepted', color: 'success', description: 'Spot confirmed' };
    case INVITATION_STATUS.DECLINED:
      return { label: 'Declined', color: 'error', description: 'Invitation declined' };
    case INVITATION_STATUS.WITHDRAWN:
      return { label: 'Withdrawn', color: 'warning', description: 'Withdrew after accepting' };
    case INVITATION_STATUS.WAITLIST:
      return { label: 'Runner-up', color: 'warning', description: 'Runner-up / championship waitlist' };
    case INVITATION_STATUS.PROMOTED:
      return { label: 'Promoted', color: 'info', description: 'Promoted from waitlist — awaiting response' };
    default:
      return { label: status, color: 'default', description: '' };
  }
};

export const DEFAULT_INVITATION_MESSAGE =
  'Congratulations! Your skater has qualified for the championship. Please review this invitation and accept or decline so we can confirm the championship roster.';

export const DEFAULT_RUNNERS_UP_MESSAGE =
  'Your skater is a runner-up for the championship and is on the waitlist. If a qualified skater declines or withdraws, we may offer this spot. No action is needed unless we contact you.';

export const getInvitationDisplayMessage = (invitation) => {
  if (!invitation) return '';
  if (invitation.status === INVITATION_STATUS.WAITLIST) {
    return invitation.runnersUpMessage || invitation.customMessage || '';
  }
  return invitation.customMessage || '';
};

export const getInvitationActor = (invitation) => {
  if (!invitation) return null;
  if (
    invitation.respondedByAdmin === true ||
    invitation.respondedByRole === 'series_manager' ||
    invitation.updatedByRole === 'series_manager'
  ) {
    return { type: 'admin', label: 'admin', name: invitation.respondedByName || '' };
  }
  if (invitation.respondedByRole === 'parent') {
    return { type: 'parent', label: 'parent', name: invitation.respondedByName || invitation.parentName || '' };
  }
  const note = String(invitation.responseNote || invitation.withdrawalReason || '');
  if (/series manager/i.test(note)) {
    return { type: 'admin', label: 'admin', name: invitation.respondedByName || '' };
  }
  if (
    invitation.respondedAt ||
    invitation.status === INVITATION_STATUS.ACCEPTED ||
    invitation.status === INVITATION_STATUS.DECLINED ||
    invitation.status === INVITATION_STATUS.WITHDRAWN
  ) {
    return { type: 'parent', label: 'parent', name: invitation.respondedByName || invitation.parentName || '' };
  }
  return null;
};

export const getInvitationResponseNote = (invitation) =>
  String(invitation?.responseNote || invitation?.withdrawalReason || '').trim();

export const getInvitationActorSummary = (invitation) => {
  const actor = getInvitationActor(invitation);
  if (!actor) return '';
  const statusDisplay = getInvitationStatusDisplay(invitation.status);
  const who = actor.name ? `${actor.label} (${actor.name})` : actor.label;
  return `${statusDisplay.label} by ${who}`;
};

const getInvitationHeaders = async ({ isAdmin = false } = {}) => {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...getImpersonationHeaders(),
  };
  if (isAdmin) {
    headers['x-admin-token'] = 'admin';
  }
  const currentUser = getAuth(app).currentUser;
  if (currentUser) {
    headers.Authorization = `Bearer ${await currentUser.getIdToken()}`;
  }
  return headers;
};

export const createInvitations = async (level, options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();
  const response = await fetch(`${baseUrl}/qualifiers/invitations`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'x-admin-token': 'admin',
    },
    body: JSON.stringify({
      level,
      includeRunnersUp: options.includeRunnersUp ?? true,
      runnersUpCount: options.runnersUpCount ?? 5,
      seasonId: options.seasonId,
      interclubId: options.interclubId,
      customMessage: options.customMessage || null,
      runnersUpMessage: options.runnersUpMessage || null,
      syncMissing: Boolean(options.syncMissing),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const getInvitations = async (options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();
  const params = new URLSearchParams();

  if (options.seasonId) params.set('seasonId', options.seasonId);
  if (options.level) params.set('level', options.level);
  if (options.status) params.set('status', options.status);
  if (options.mySkaters) params.set('mySkaters', 'true');

  const headers = await getInvitationHeaders({ isAdmin: options.isAdmin });

  const response = await fetch(`${baseUrl}/qualifiers/invitations?${params.toString()}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const getInvitationSummary = async (seasonId) => {
  const baseUrl = await getApiBaseUrlWithFallback();
  const params = new URLSearchParams();
  if (seasonId) params.set('seasonId', seasonId);

  const response = await fetch(`${baseUrl}/qualifiers/invitations/summary?${params.toString()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-admin-token': 'admin',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const updateInvitationStatus = async (invitationId, status, note = null, options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();

  const response = await fetch(`${baseUrl}/qualifiers/invitations/${invitationId}`, {
    method: 'PUT',
    headers: await getInvitationHeaders({ isAdmin: Boolean(options.isAdmin) }),
    body: JSON.stringify({ status, note }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const updateInvitationMessage = async (level, options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();

  const response = await fetch(`${baseUrl}/qualifiers/invitations/${encodeURIComponent(level)}/message`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-admin-token': 'admin',
    },
    body: JSON.stringify({
      seasonId: options.seasonId || null,
      customMessage: options.customMessage || '',
      runnersUpMessage: options.runnersUpMessage || '',
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const sendInvitations = async (level, options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();
  const seasonId = typeof options === 'string' ? options : options.seasonId;
  const customMessage = typeof options === 'string' ? null : options.customMessage;
  const runnersUpMessage = typeof options === 'string' ? null : options.runnersUpMessage;

  const response = await fetch(`${baseUrl}/qualifiers/invitations/${encodeURIComponent(level)}/send`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-admin-token': 'admin',
    },
    body: JSON.stringify({
      seasonId,
      customMessage: customMessage || null,
      runnersUpMessage: runnersUpMessage || null,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const acceptInvitation = async (invitationId, options = {}) => {
  return updateInvitationStatus(invitationId, INVITATION_STATUS.ACCEPTED, options.note || null, options);
};

export const declineInvitation = async (invitationId, reason = null, options = {}) => {
  return updateInvitationStatus(invitationId, INVITATION_STATUS.DECLINED, reason, options);
};

export const withdrawFromChampionship = async (invitationId, reason = null, options = {}) => {
  return updateInvitationStatus(invitationId, INVITATION_STATUS.WITHDRAWN, reason, options);
};

export const setGenderSplit = async (level, split, options = {}) => {
  const baseUrl = await getApiBaseUrlWithFallback();
  const headers = await getInvitationHeaders({ isAdmin: options.isAdmin !== false });
  const response = await fetch(`${baseUrl}/qualifiers/gender-splits`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ level, split }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export { INVITATION_STATUS };
